import { FC } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';
import { Element } from 'react-scroll';

import Container from '@components/store/container';
import useCourseDetail from '@hooks/course/useCourseDetail';

type ProductCriteriaCertificateProps = {
  scrollElementName: string;
  bgColor?: string;
};

const ProductCriteriaCertificate: FC<ProductCriteriaCertificateProps> = ({ scrollElementName, bgColor }) => {
  const { t } = useTranslation('store_course');
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const { criteriaCertificate } = useCourseDetail();

  return (
    <Element name={scrollElementName}>
      <Box ref={ref} minHeight={{ xs: '570px', md: '364px', lg: '400px' }} py={10} bgcolor={bgColor}>
        {inView ? (
          <Container
            containerSx={{
              px: { xs: 3, lg: 0 },
              display: 'grid',
              gridTemplateColumns: { md: '1fr 1fr', lg: '1fr 30%' },
              gap: { xs: 5, lg: 8 },
            }}
          >
            <Box>
              <Typography variant="h4">{t('course_detail.course_certificate.title')}</Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Typography variant="h5">{t('course_detail.course_certificate.criteria_receive_certificate')}</Typography>
              <Typography variant="body1lanna" whiteSpace="pre-line">
                {t('course_detail.course_certificate.criteria_receive_certificate.description')}
              </Typography>

              {criteriaCertificate?.learnPercent ? (
                <Typography variant="body1lanna" component="p">
                  {t('course_detail.course_certificate.criteria_receive_certificate.learning_progress_criteria', {
                    learnPercent: criteriaCertificate?.learnPercent,
                  })}
                </Typography>
              ) : null}

              {criteriaCertificate?.assessmentsScore && criteriaCertificate.assessmentsScore.length > 0
                ? criteriaCertificate.assessmentsScore.map((assessmentScore) => (
                    <Typography key={assessmentScore.quizName} variant="body1lanna" component="p">
                      {t('course_detail.course_certificate.criteria_receive_certificate.assessment_criteria', {
                        quizName: assessmentScore.quizName,
                        score: assessmentScore.score,
                      })}
                    </Typography>
                  ))
                : null}
            </Box>
            <Box component="img" src="/static/images/course/course-certificate.svg" width="100%" />
          </Container>
        ) : null}
      </Box>
    </Element>
  );
};

export default ProductCriteriaCertificate;
